import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { alertMonitoringApi } from '../api/adminAlert.api';
import PageHeader from '../components/PageHeader';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:9085';

const LiveAlertMonitor = () => {
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const [resolving, setResolving] = useState(false);
  const socketRef = useRef(null);

  useEffect(() => {
    fetchInitialData();
    setupWebSocket();

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [alertsRes, statsRes] = await Promise.all([
        alertMonitoringApi.getActive(),
        alertMonitoringApi.getStats()
      ]);
      
      if (alertsRes.success) setActiveAlerts(alertsRes.data);
      if (statsRes.success) setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupWebSocket = () => {
    const token = localStorage.getItem('adminToken');
    if (!token) return;

    socketRef.current = io(BASE_URL, {
      auth: { token }
    });

    socketRef.current.on('connect', () => {
      console.log('Admin WebSocket connected for live monitor');
    });

    socketRef.current.on('alert:new', (data) => {
      console.log('New alert received:', data);
      setActiveAlerts(prev => [data.alert, ...prev]);
      // Refresh stats
      alertMonitoringApi.getStats().then(res => {
        if (res.success) setStats(res.data);
      });
    });

    socketRef.current.on('alert:updated', (data) => {
      console.log('Alert status updated:', data);
      setActiveAlerts(prev => prev.map(alert => {
        if (alert.id === data.alertId) {
          // If resolved, we could remove it or just show as resolved
          return { ...alert, status: data.status };
        }
        return alert;
      }).filter(alert => !['resolved', 'cancelled'].includes(alert.status))); // optionally filter out resolved
      
      if (selectedAlert && selectedAlert.id === data.alertId) {
        setSelectedAlert(prev => ({ ...prev, status: data.status }));
      }
    });
  };

  const handleForceResolve = async () => {
    if (!selectedAlert) return;
    try {
      setResolving(true);
      const res = await alertMonitoringApi.resolveAlert(selectedAlert.id, resolveNotes);
      if (res.success) {
        // Will be updated via WebSocket, but update locally for speed
        setActiveAlerts(prev => prev.filter(a => a.id !== selectedAlert.id));
        setSelectedAlert(null);
        setResolveNotes('');
      }
    } catch (error) {
      alert('Failed to resolve alert: ' + (error.response?.data?.message || error.message));
    } finally {
      setResolving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'triggered': return 'bg-red-100 text-red-800 border-red-200';
      case 'active': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'escalated': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'acknowledged': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) return <div className="p-6">Loading live monitor...</div>;

  return (
    <>
      <PageHeader
        title={
          <div className="flex items-center">
            <span className="relative flex h-3 w-3 mr-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
            </span>
            Live Alert Monitor
          </div>
        }
        trail={[{ label: 'Alerts' }, { label: 'Live Monitor' }]}
      />

      {/* STAT CARDS */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow p-5 border border-gray-100 border-l-4 border-l-red-500">
            <h3 className="text-sm font-medium text-gray-500 uppercase">Active Alerts Now</h3>
            <p className="mt-2 text-3xl font-bold text-gray-900">{stats.activeCount}</p>
          </div>
          <div className="bg-white rounded-xl shadow p-5 border border-gray-100 border-l-4 border-l-orange-500">
            <h3 className="text-sm font-medium text-gray-500 uppercase">Alerts Today</h3>
            <p className="mt-2 text-3xl font-bold text-gray-900">{stats.todayCount}</p>
          </div>
          <div className="bg-white rounded-xl shadow p-5 border border-gray-100 border-l-4 border-l-blue-500">
            <h3 className="text-sm font-medium text-gray-500 uppercase">Avg Ack Time</h3>
            <p className="mt-2 text-3xl font-bold text-gray-900">{stats.avgAckTime}</p>
          </div>
          <div className="bg-white rounded-xl shadow p-5 border border-gray-100 border-l-4 border-l-purple-500">
            <h3 className="text-sm font-medium text-gray-500 uppercase">False Alarm Rate (30d)</h3>
            <p className="mt-2 text-3xl font-bold text-gray-900">{stats.falseAlarmRate}</p>
          </div>
        </div>
      )}

      {/* ACTIVE ALERTS LIST */}
      <div className="bg-white rounded-xl shadow overflow-hidden border border-gray-100 mb-8">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-800">Current Active Emergencies</h2>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-white">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Alert ID / Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time Active</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {activeAlerts.map((alert) => (
              <tr key={alert.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{alert.type.replace('_', ' ').toUpperCase()}</div>
                  <div className="text-xs text-gray-500 font-mono mt-1">{alert.id.substring(0, 8)}...</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {alert.user ? (
                    <>
                      <div className="text-sm text-gray-900 font-medium">{alert.user.firstName} {alert.user.lastName}</div>
                      <div className="text-xs text-gray-500">{alert.user.mobile}</div>
                    </>
                  ) : (
                    <div className="text-sm text-gray-500 italic">Unknown User</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getStatusColor(alert.status)}`}>
                    {alert.status.toUpperCase()}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(alert.createdAt).toLocaleTimeString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button 
                    onClick={() => setSelectedAlert(alert)}
                    className="text-indigo-600 hover:text-indigo-900 bg-indigo-50 px-3 py-1 rounded-md"
                  >
                    View Details
                  </button>
                </td>
              </tr>
            ))}
            {activeAlerts.length === 0 && (
              <tr>
                <td colSpan="5" className="px-6 py-10 text-center text-gray-500">
                  <div className="flex flex-col items-center justify-center">
                    <i className="fa-solid fa-shield-check text-4xl text-green-400 mb-3"></i>
                    <p className="text-lg font-medium text-gray-900">All Clear</p>
                    <p className="text-sm">No active emergencies on the network right now.</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ALERT DETAIL MODAL */}
      {selectedAlert && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
              <h3 className="text-lg font-bold text-gray-900">Emergency Details</h3>
              <button onClick={() => setSelectedAlert(null)} className="text-gray-400 hover:text-gray-500">
                <span className="sr-only">Close</span>
                <i className="fa-solid fa-xmark text-xl"></i>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Alert Info</h4>
                  <p className="text-sm text-gray-900"><span className="font-medium">Type:</span> {selectedAlert.type}</p>
                  <p className="text-sm text-gray-900"><span className="font-medium">Triggered:</span> {new Date(selectedAlert.createdAt).toLocaleString()}</p>
                  <p className="text-sm text-gray-900"><span className="font-medium">Status:</span> {selectedAlert.status}</p>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">User Info</h4>
                  {selectedAlert.user && (
                    <>
                      <p className="text-sm text-gray-900"><span className="font-medium">Name:</span> {selectedAlert.user.firstName} {selectedAlert.user.lastName}</p>
                      <p className="text-sm text-gray-900"><span className="font-medium">Phone:</span> {selectedAlert.user.mobile}</p>
                      <p className="text-sm text-gray-900"><span className="font-medium">ID:</span> {selectedAlert.user.id}</p>
                    </>
                  )}
                </div>
              </div>

              {selectedAlert.locationLat && selectedAlert.locationLng && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Location</h4>
                  <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center text-gray-500 border border-gray-200">
                    <p><i className="fa-solid fa-map-location-dot mr-2"></i>Map tracking would render here<br/>
                    Lat: {selectedAlert.locationLat}, Lng: {selectedAlert.locationLng}</p>
                  </div>
                </div>
              )}

              <div className="mt-8 border-t border-gray-200 pt-6">
                <h4 className="text-sm font-semibold text-red-600 uppercase tracking-wider mb-4">Admin Override: Force Resolve</h4>
                <p className="text-sm text-gray-600 mb-3">If the alert is stuck and no contacts are responding, you can manually resolve it.</p>
                <textarea 
                  value={resolveNotes}
                  onChange={(e) => setResolveNotes(e.target.value)}
                  placeholder="Enter reason for force resolving..."
                  className="w-full border border-gray-300 rounded-md shadow-sm p-3 text-sm mb-3 focus:ring-red-500 focus:border-red-500"
                  rows="3"
                ></textarea>
                <button
                  onClick={handleForceResolve}
                  disabled={resolving || !resolveNotes.trim()}
                  className={`px-4 py-2 text-white text-sm font-medium rounded-md shadow-sm ${resolving || !resolveNotes.trim() ? 'bg-red-300 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'}`}
                >
                  {resolving ? 'Resolving...' : 'Force Resolve Alert'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LiveAlertMonitor;
